import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useVariantExplorer } from "./hooks/useVariantExplorer";
import VariantTable from "./components/VariantTable";
import { SORT_OPTIONS } from "./constants";
import BaseTile from "../../../components/Tile/BaseTile";
import TileHeader, {
  TileHeaderControls,
  TileHeaderSelect,
  TileHeaderButton,
} from "../../../components/Tile/TileHeader";
import TileContent, { TileStatusContent } from "../../../components/Tile/TileContent";
import { useAutoLoad } from "../../../hooks/useAutoLoad";
import { cn } from "../../../utils/cn";
import { useGlobalAPI } from "../../../hooks/useGlobalAPI";
import { makeRequest } from "../../../services/apiClient";
import CoverageBar from "./components/CoverageBar";
import MultiSelectDropdown from "../../../components/ui/MultiSelectDropdown";
// import CoverageSlider from "./components/CoverageSlider";

// Modern Pagination Component
const Pagination = ({
  currentPage,
  totalPages,
  setCurrentPage,
  getPageGroup,
}) => {
  const pageGroup = getPageGroup();
  const minPageInGroup = pageGroup[0];
  const maxPageInGroup = pageGroup[pageGroup.length - 1];

  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-center mt-6 mb-4">
      {/* Pagination Controls */}
      <div className="flex items-center space-x-1">
        {/* First Page */}
        {currentPage > 2 && (
          <>
            <button
              onClick={() => setCurrentPage(1)}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors duration-200"
              title="First page"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                />
              </svg>
            </button>
            {minPageInGroup > 2 && (
              <span className="px-2 py-2 text-gray-400">...</span>
            )}
          </>
        )}

        {/* Previous Page */}
        {currentPage > 1 && (
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors duration-200"
            title="Previous page"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        )}

        {/* Page Numbers */}
        {pageGroup.map((num) => (
          <button
            key={num}
            onClick={() => setCurrentPage(num)}
            className={cn(
              "inline-flex items-center px-4 py-2 text-sm font-medium transition-colors duration-200 rounded-md min-w-[2.5rem] justify-center",
              num === currentPage
                ? "bg-blue-600 text-white border border-blue-600 shadow-sm"
                : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-900"
            )}
          >
            {num}
          </button>
        ))}

        {/* Next Page */}
        {currentPage < totalPages && (
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors duration-200"
            title="Next page"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        )}

        {/* Last Page */}
        {currentPage < totalPages - 1 && (
          <>
            {maxPageInGroup < totalPages - 1 && (
              <span className="px-2 py-2 text-gray-400">...</span>
            )}
            <button
              onClick={() => setCurrentPage(totalPages)}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors duration-200"
              title="Last page"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 5l7 7-7 7M5 5l7 7-7 7"
                />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const VariantExplorer = ({ activeFile, onRefresh }) => {
  const api = useGlobalAPI(activeFile);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initLoading, setInitLoading] = useState(false);
  const [initError, setInitError] = useState(null);
  const [objectTypes, setObjectTypes] = useState([]);
  const [selectedObjectType, setSelectedObjectType] = useState("");
  const [sliderPercent, setSliderPercent] = useState(100);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [matchingRule, setMatchingRule] = useState("");
  const [isActivityFilterActive, setIsActivityFilterActive] = useState(false);
  const [preActivityFilterSelection, setPreActivityFilterSelection] = useState([]);
  const [coverageBeforeActivityFilter, setCoverageBeforeActivityFilter] = useState(null);

  const {
    loading,
    isFiltered,
    isVariantFilterActive,
    error,
    hasLoaded,
    variants,
    summary,
    selectedVariants,
    currentVariants,
    totalPages,
    currentPage,
    sortKey,
    sortOrder,
    handleToggleSelect,
    toggleSelectAll,
    handleBulkSelection,
    handleSortChange,
    handleColumnSort,
    handleFilter,
    handleReset,
    setCurrentPage,
    getPageGroup,
    refetchData,
  } = useVariantExplorer(selectedObjectType, activeFile);

  // Extract unique activities from all variants
  const uniqueActivities = useMemo(() => {
    const activities = new Set();
    variants.forEach(variant => {
      if (variant.activities && Array.isArray(variant.activities)) {
        variant.activities.forEach(activity => {
          if (activity && typeof activity === 'string') {
            activities.add(activity);
          }
        });
      }
    });
    return Array.from(activities).sort();
  }, [variants]);


  // Reload when global filters change (if already initialized)
  useEffect(() => {
    if (isInitialized && activeFile) {
      console.log(
        "🔄 VariantExplorer: Global filters changed, reloading metadata..."
      );
      handleInitialize();
    }
  }, [api.currentFilters, activeFile, isInitialized]);

  // Reload variant data when global filters change but object type selection is stable
  useEffect(() => {
    if (isInitialized && selectedObjectType && activeFile && refetchData) {
      console.log(
        "🔄 VariantExplorer: Global filters changed, refetching variant data..."
      );
      refetchData();
    }
  }, [api.currentFilters]);

  // Reset selected object type if it's no longer in the available list
  useEffect(() => {
    if (
      objectTypes.length > 0 &&
      selectedObjectType &&
      !objectTypes.includes(selectedObjectType)
    ) {
      console.log(
        `🔄 VariantExplorer: Current selection '${selectedObjectType}' not in filtered list. Resetting to '${objectTypes[0]}'`
      );
      setSelectedObjectType(objectTypes[0]);
    }
  }, [objectTypes, selectedObjectType]);

  const handleInitialize = async () => {
    setInitLoading(true);
    setInitError(null);
    try {
      // Load all object types using fast cached endpoint
      // Object types are independent of variant selection, so use direct API call
      // to avoid sending large variant filters that cause 431 errors
      const params = { file_type: activeFile || "main" };
      const response = await makeRequest(
        "/object-types",
        { params },
        "Object Types"
      );

      const allTypes = Array.isArray(response.objectTypes)
        ? response.objectTypes
        : response;

      // Apply client-side filtering based on current global filters (excluding variant filter)
      const cleanFilters = { ...api.currentFilters };
      delete cleanFilters.variantFilter;
      const activeFilters = cleanFilters;
      let filteredTypes = allTypes;

      // Apply object type filter if specified
      if (activeFilters.objectTypes && activeFilters.objectTypes.length > 0) {
        filteredTypes = allTypes.filter((type) =>
          activeFilters.objectTypes.includes(type)
        );
      }

      console.log("📊 VariantExplorer metadata loaded:", {
        allTypes: allTypes.length,
        filteredTypes: filteredTypes.length,
        hasFilters:
          activeFilters.objectTypes && activeFilters.objectTypes.length > 0,
      });

      if (!filteredTypes || filteredTypes.length === 0) {
        throw new Error("No object types were found for the current filters.");
      }

      setObjectTypes(filteredTypes);

      // If the currently selected object type is no longer available, select the preferred default
      // Always update if current selection is not valid for filtered types
      if (!selectedObjectType || !filteredTypes.includes(selectedObjectType)) {
        // Try to default to "Kundenauftragsposition" if available
        const preferredType = "Kundenauftragsposition";
        const newObjectType = filteredTypes.includes(preferredType)
          ? preferredType
          : filteredTypes[0];  // Fallback to first item if preferred type not found

        console.log(
          `🔄 VariantExplorer: Setting object type from '${selectedObjectType}' to '${newObjectType}'` +
          (newObjectType === preferredType ? ' (preferred default)' : ' (fallback to first)')
        );
        setSelectedObjectType(newObjectType); // This triggers the data fetch in the hook
      }

      setIsInitialized(true);
    } catch (err) {
      console.error("Failed to initialize Variant Explorer:", err);
      setInitError("Failed to load initial configuration. Please try again.");
    } finally {
      setInitLoading(false);
    }
  };

  // Apply initial slider selection after variants are loaded
  useEffect(() => {
    if (variants.length > 0) {
      const sortedVariants = [...variants].sort((a, b) => {
        let aVal = a[sortKey];
        let bVal = b[sortKey];
        if (sortKey === "variant_id") {
          return sortOrder === "asc"
            ? a.variant_id.localeCompare(b.variant_id)
            : b.variant_id.localeCompare(a.variant_id);
        }
        if (sortKey === "median_lead_time") {
          aVal = a.lead_time_stats?.median ?? 0;
          bVal = b.lead_time_stats?.median ?? 0;
        }
        aVal = aVal ?? 0;
        bVal = bVal ?? 0;
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      });

      const variantsToSelect = Math.max(
        1,
        Math.floor((sliderPercent / 100) * sortedVariants.length)
      );
      const selectedFromSlider = sortedVariants.slice(0, variantsToSelect);
      const expectedSelectedIds = selectedFromSlider.map((v) => v.variant_id);

      const currentSelectionSet = new Set(selectedVariants);
      const expectedSelectionSet = new Set(expectedSelectedIds);

      const selectionChanged =
        currentSelectionSet.size !== expectedSelectionSet.size ||
        [...expectedSelectionSet].some((id) => !currentSelectionSet.has(id));

      if (selectionChanged) {
        console.log("🔄 Applying slider selection:", {
          percentage: sliderPercent,
          variantsToSelect,
          selectedIds: expectedSelectedIds,
        });
        handleSliderSelection(expectedSelectedIds);
      }
    }
  }, [variants, sortKey, sortOrder, sliderPercent]);

  const totalCases = summary?.total_objects || 0;

  const selectedCases = useMemo(() => {
    return variants
      .filter((v) => selectedVariants.includes(v.variant_id))
      .reduce((sum, v) => sum + v.trace_count, 0);
  }, [variants, selectedVariants]);

  const percentCovered = useMemo(() => {
    return totalCases > 0 ? (selectedCases / totalCases) * 100 : 0;
  }, [selectedCases, totalCases]);

  const allVariantsSelected = selectedVariants.length === variants.length;
  const isFilteringEverything = !loading && allVariantsSelected;
  const hasData = isInitialized || variants.length > 0;

  // Auto-load data when persistent cache is enabled
  useAutoLoad(handleInitialize, [activeFile], hasData);

  // Provide refresh function to parent (GridComponent)
  React.useEffect(() => {
    if (onRefresh) {
      onRefresh(handleInitialize);
    }
  }, [onRefresh, handleInitialize]);

  const unselectedVariants = variants.filter(
    (v) => !selectedVariants.includes(v.variant_id)
  );

  const othersCases = unselectedVariants.reduce(
    (sum, v) => sum + v.trace_count,
    0
  );

  const othersPercentage =
    totalCases > 0 ? (othersCases / totalCases) * 100 : 0;

  const weightedLeadTimes = [];
  unselectedVariants.forEach((v) => {
    const median = parseFloat(v.lead_time_stats?.median);
    const count = v.trace_count;
    if (!isNaN(median) && count > 0) {
      weightedLeadTimes.push(...Array(count).fill(median));
    }
  });

  const noUnselected = unselectedVariants.length === 0;
  const medianLeadTime =
    weightedLeadTimes.length > 0
      ? weightedLeadTimes.sort((a, b) => a - b)[
          Math.floor(weightedLeadTimes.length / 2)
        ]
      : null;

  const others = {
    object_count: noUnselected ? "–" : othersCases,
    percentage: othersPercentage,
    num_steps: noUnselected ? "–" : "-",
    median_lead_time: noUnselected ? "–" : medianLeadTime ?? "–",
  };

  const isOverallLoading =
    initLoading || loading;
  const showVariantContent = isInitialized && hasLoaded && !loading && !error;
  const hasVariants = variants.length > 0;

  const handleSliderSelection = useCallback(
    (variantIds) => {
      handleBulkSelection(variantIds);
    },
    [handleBulkSelection]
  );

  const handleActivitySelection = (activities) => {
    setSelectedActivities(activities);
  };

  // Apply activity filter
  const handleActivityFilter = async () => {
    if (selectedActivities.length === 0) return;

    // Save current selection state before applying activity filter
    if (!isActivityFilterActive) {
      setPreActivityFilterSelection([...selectedVariants]);
      
      // Save current coverage BEFORE activity filter changes selectedVariants
      const currentSelectedCases = variants
        .filter((v) => selectedVariants.includes(v.variant_id))
        .reduce((sum, v) => sum + v.trace_count, 0);
      const currentPercentCovered = totalCases > 0 ? (currentSelectedCases / totalCases) * 100 : 0;
      setCoverageBeforeActivityFilter({
        selectedCases: currentSelectedCases,
        percentCovered: currentPercentCovered,
      });
    }

    // Create activity filter object for backend
    const activityFilterObj = {
      enabled: true,
      activities: selectedActivities,
      matchType: matchingRule === "any" ? "any" : "all"
    };

    await refetchData(activityFilterObj);

    setIsActivityFilterActive(true);
    console.log(`✅ Activity filter applied with matchType="${matchingRule}" on activities:`, selectedActivities);
  };

  // Reset activity filter independently without affecting main filter
  const handleActivityReset = async () => {
    console.log('🔄 Resetting activity filter...');
    
    if (isActivityFilterActive && preActivityFilterSelection.length > 0) {
      // Restore previous selection before activity filter was applied
      handleBulkSelection(preActivityFilterSelection);
      console.log(`✅ Restored previous selection: ${preActivityFilterSelection.length} variants`);
    } else {
      // If no previous selection, select all variants
      handleBulkSelection(variants.map((v) => v.variant_id));
      console.log(`✅ Selecting all variants: ${variants.length} total`);
    }
    await refetchData(null);

    setIsActivityFilterActive(false);
    setSelectedActivities([]);
    setMatchingRule("any");
    setPreActivityFilterSelection([]);
    setCoverageBeforeActivityFilter(null);
    
    console.log('✅ Activity filter cleared, showing all variants');
  };

  return (
    <BaseTile
      loading={isOverallLoading}
      error={initError || error}
      minHeight="xl"
      className="h-full mb-6"
    >
      {!isInitialized ? (
        <TileContent>
          <TileStatusContent
            status="info"
            title="Load Variant Data"
            message="Click the button to load the variant explorer and its options."
            action={
              <TileHeaderButton
                variant="primary"
                size="sm"
                onClick={handleInitialize}
                disabled={initLoading}
              >
                {initLoading ? "Loading..." : "Load Data"}
              </TileHeaderButton>
            }
          />
        </TileContent>
      ) : (
        <>
          {!isOverallLoading && (
            <TileHeader
              layout="between"
              size="default"
              controls={
                <TileHeaderControls className="flex flex-wrap gap-4 items-end">
                  <TileHeaderSelect
                    label="Object Type"
                    value={selectedObjectType}
                    onChange={(e) => setSelectedObjectType(e.target.value)}
                    disabled={loading}
                    options={objectTypes.map((type) => ({
                      value: type,
                      label: type,
                    }))}
                  />
                  <TileHeaderSelect
                    label="Sort"
                    onChange={(e) => handleSortChange(e.target.value)}
                    disabled={loading}
                    options={SORT_OPTIONS.map((opt) => ({
                      value: opt,
                      label: opt,
                    }))}
                  />
                  <div className="flex flex-col gap-1 min-w-[200px]">
                    <label className="text-xs font-medium text-gray-700">
                      Variants Coverage ({sliderPercent}%)
                    </label>
                    <input
                      type="range"
                      min={1}
                      max={100}
                      step={1}
                      value={sliderPercent}
                      onChange={(e) => {
                        setSliderPercent(Number(e.target.value));
                        if (variants.length > 0) {
                          const sortedVariants = [...variants].sort((a, b) => {
                            let aVal = a[sortKey];
                            let bVal = b[sortKey];

                            if (sortKey === "variant_id") {
                              return sortOrder === "asc"
                                ? a.variant_id.localeCompare(b.variant_id)
                                : b.variant_id.localeCompare(a.variant_id);
                            }

                            if (sortKey === "median_lead_time") {
                              aVal = a.lead_time_stats?.median ?? 0;
                              bVal = b.lead_time_stats?.median ?? 0;
                            }

                            aVal = aVal ?? 0;
                            bVal = bVal ?? 0;

                            return sortOrder === "asc"
                              ? aVal - bVal
                              : bVal - aVal;
                          });

                          const variantsToSelect = Math.max(
                            1,
                            Math.floor(
                              (Number(e.target.value) / 100) *
                                sortedVariants.length
                            )
                          );
                          const selectedFromSlider = sortedVariants.slice(
                            0,
                            variantsToSelect
                          );
                          const selectedIds = selectedFromSlider.map(
                            (v) => v.variant_id
                          );
                          handleSliderSelection(selectedIds);
                        }
                      }}
                      disabled={loading}
                      className="h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-thumb:appearance-none slider-thumb:h-4 slider-thumb:w-4 slider-thumb:rounded-full slider-thumb:bg-primary-500 slider-thumb:cursor-pointer slider-thumb:shadow-sm hover:slider-thumb:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50"
                      style={{
                        background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${sliderPercent}%, #E5E7EB ${sliderPercent}%, #E5E7EB 100%)`,
                      }}
                    />
                  </div>
                </TileHeaderControls>
              }
              actions={
                <div className="flex gap-2">
                  <TileHeaderButton
                    variant="primary"
                    size="sm"
                    onClick={handleFilter}
                    disabled={loading || selectedVariants.length === 0}
                  >
                    Filter
                  </TileHeaderButton>
                  {summary && summary.total_variants && (
                    <TileHeaderButton
                      variant="secondary"
                      size="sm"
                      onClick={handleReset}
                      disabled={loading || !isVariantFilterActive}
                    >
                      Reset
                    </TileHeaderButton>
                  )}
                </div>
              }
            />
          )}

          <TileContent layout="flex" direction="column" grow>
            {isOverallLoading && (
              <TileStatusContent
                status="loading"
                title="Loading Variants"
                message="Loading variant data for the selected object type..."
              />
            )}

            {showVariantContent && (
              <>
                {/* Activity Filter Controls - ALWAYS VISIBLE */}
                <div className="border-b border-gray-200 p-4">
                  <div className="flex items-end justify-end gap-3 flex-nowrap">
                    <div className="flex-shrink-0 min-w-[280px]">
                      <MultiSelectDropdown
                        label="Activities"
                        labelPosition="inline"
                        options={uniqueActivities}
                        selectedValues={selectedActivities}
                        onSelectionChange={handleActivitySelection}
                        placeholder={uniqueActivities.length === 0 ? "Loading activities..." : "Select activities"}
                        disabled={loading || uniqueActivities.length === 0}
                        minOptionWidth="450px"
                        className="w-full"
                      />
                    </div>

                    <div className="flex-shrink-0 min-w-[200px]">
                      <TileHeaderSelect
                        label="Matching Rule"
                        value={matchingRule}
                        onChange={(e) => setMatchingRule(e.target.value)}
                        disabled={loading || selectedActivities.length === 0}
                        placeholder="Select matching rule..."
                        options={[
                          {
                            value: "any",
                            label: "Match any selected activity",
                          },
                          {
                            value: "all",
                            label: "Match all selected activities",
                          },
                        ]}
                        className="w-full"
                        title={selectedActivities.length === 0 ? "Please select at least one activity first" : ""}
                      />
                    </div>

                    <div className="flex-shrink-0 flex gap-2 min-w-[300px]">
                      <TileHeaderButton
                        variant="primary"
                        size="sm"
                        onClick={handleActivityFilter}
                        disabled={
                          loading || selectedActivities.length === 0
                        }
                        title="Filter variants by selected activities"
                        className="whitespace-nowrap"
                      >
                        Apply Activity Filter
                      </TileHeaderButton>
                      <TileHeaderButton
                        variant="secondary"
                        size="sm"
                        onClick={handleActivityReset}
                        disabled={loading || !isActivityFilterActive}
                        title="Clear activity filter and show all variants"
                        className="whitespace-nowrap"
                      >
                        Reset Activities
                      </TileHeaderButton>
                    </div>
                  </div>
                </div>

                {/* Content Area - Either Table or Empty State */}
                {hasVariants ? (
                  <>
                    <CoverageBar
                      percentCovered={percentCovered}
                      selectedVariantsCount={selectedVariants.length}
                      totalVariants={summary?.total_variants}
                      selectedCases={selectedCases}
                      totalCases={summary?.total_objects}
                    />

                    <div className="flex-grow overflow-hidden">
                      <VariantTable
                        variants={variants}
                        currentVariants={currentVariants}
                        selectedVariants={selectedVariants}
                        handleToggleSelect={handleToggleSelect}
                        toggleSelectAll={toggleSelectAll}
                        sortKey={sortKey}
                        sortOrder={sortOrder}
                        handleColumnSort={handleColumnSort}
                        loading={loading}
                        others={others}
                      />
                    </div>

                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      setCurrentPage={setCurrentPage}
                      getPageGroup={getPageGroup}
                    />
                  </>
                ) : (
                  <TileStatusContent
                    status="empty"
                    title="No Variants Available"
                    message={
                      isActivityFilterActive
                        ? `No variants match the selected ${matchingRule === "any" ? "any of the" : ""} activities.`
                        : "No variant data available for this object type."
                    }
                  />
                )}
              </>
            )}
          </TileContent>
        </>
      )}
    </BaseTile>
  );
};

export default VariantExplorer;